import { Box, darken, Icon, SxProps, Theme, Typography, useTheme } from '@mui/material';
import React, { useState, useEffect, useRef, MouseEvent } from 'react';
import {
  CircleMarker,
  DivIcon,
  GridLayer,
  LayerGroup,
  Marker,
  Point,
  Polygon,
  Polyline,
  TileLayer
} from 'leaflet';
import {
  CheckBoxOutlineBlank,
  CheckBoxOutlined,
  CircleOutlined,
  ErrorOutlineOutlined,
  IndeterminateCheckBoxOutlined,
  MyLocationOutlined,
  PinDropOutlined,
  PolylineOutlined,
  RectangleOutlined,
  WallpaperOutlined
} from '@mui/icons-material';
import { LayerContextMenu } from './layerOptions';
import { HidroLayer, HidroLayerGroup } from './layers';
import { PaintLineString, PaintPolygon, PaintTypes } from '../style';
import { PolyMancha } from '../../barragem';
import { PolyZas, PolyZss } from '../../zas';


const heightComponent = 1.375;
export const timeAnim = 0.3;
const dividerItems = (<div style={{ width: "0.188rem" }} />);

const iconStyle: SxProps<Theme> = {
  fontSize: 18,
  width: 15,
  height: 15,
  padding: 0,
  margin: 0,
  opacity: undefined,
};
const titleStyle = (theme: Theme): SxProps<Theme> => {
  const bgHovered = darken(theme.palette.background.paper, 0.5);

  return {
    height: `${heightComponent}rem`,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: "0.438rem",
    paddingRight: "0.438rem",
    borderRadius: "0.188rem",
    transition: `background-color ${timeAnim}s`,
    '&:hover': {
      backgroundColor: bgHovered,
    },
  };
};

interface PropsLayerHead {
  layer: HidroLayer | HidroLayerGroup;
  expand?: boolean;
  onExpand?: () => void;
  maxWidth?: string | number;
  width?: string | number;
}
export const LayerHead: React.FC<PropsLayerHead> = (data) => {
  // Theme
  const theme = useTheme();

  const containerRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ show: boolean; x: number; y: number; }>({ show: false, x: 0, y: 0 });


  const setCheck = () => {
    if (contextMenu.show) return;

    data.layer.showing = !(data.layer.showing ?? true);
    setState(!state);
  };

  const closeContextMenu = () => {
    setContextMenu({ ...contextMenu, show: false });
  };

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu({ show: true, x: event.clientX, y: event.clientY - 60 });
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClickOutside = (event: any) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setContextMenu({ ...contextMenu, show: false });
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.addEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const mother = data.layer.getMother();
  let motherShowing = true;
  if (mother && mother.showing === false) {
    motherShowing = false;
  }

  return <Box
    ref={containerRef}
    onClick={contextMenu.show ? closeContextMenu : undefined}
    onContextMenu={handleContextMenu}
    sx={{
      ...titleStyle(theme),
      maxWidth: data.maxWidth,
      width: data.width,
      cursor: "pointer",
    }}
  >
    {getCheck(data.layer.showing, motherShowing, setCheck)}
    {dividerItems}

    <Box
      onClick={
        contextMenu.show ?
          closeContextMenu : (
            data.layer instanceof HidroLayerGroup ?
              data.onExpand : setCheck
          )
      }
      sx={{
        ...titleStyle(theme),
        width: "100%",
        cursor: "pointer",
      }}>
      {getIcon(data, motherShowing)}
      {dividerItems}

      <Typography
        fontSize={14}
        style={{
          opacity: motherShowing ? 1 : 0.5,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
          fontWeight: (data.layer instanceof HidroLayerGroup) ? "600" : "400",
        }}>
        {data.layer.name}
      </Typography>

      <div style={{ flex: 1 }} />
      <LayerContextMenu
        layer={data.layer}
        x={contextMenu.x}
        y={contextMenu.y}
        show={contextMenu.show}
        onClose={closeContextMenu} />
    </Box>
  </Box >;
};

// TODO: Implementar os estilos personalizados por valores
const getIcon = (data: PropsLayerHead, motherShowing: boolean, setCheck?: () => void) => {

  if (data.layer instanceof HidroLayerGroup) {
    return (
      <Icon
        onClick={data.onExpand}
        sx={{
          ...iconStyle,
          transform: `rotate(${data.expand ? -45 : -90}deg)`,
          transition: `transform ${timeAnim}s ease-in-out`
        }}
      >
        arrow_drop_down
      </Icon>
    );
  }

  iconStyle.opacity = motherShowing ? 1 : 0.5;
  let iconComponent = <></>;

  const layer = data.layer.layer;
  let styleIcon = data.layer.style;
  if ("paintOptions" in layer) {
    styleIcon = layer.paintOptions as PaintTypes;
  }

  if (layer instanceof Polygon || layer instanceof PolyMancha || layer instanceof PolyZas || layer instanceof PolyZss) {
    const style = styleIcon as PaintPolygon;

    let newStyle: SxProps = { ...(iconStyle as SxProps) };
    if (style?.weight && style?.color) {
      newStyle = {
        ...newStyle,
        border: `${style.weight ?? 15}px solid ${style.color ?? "black"}`
      };
    }

    if (style?.fillColor) {
      newStyle = {
        ...newStyle,
        backgroundColor: style.fillColor,
      };
    }

    iconComponent = <Box sx={newStyle} />;
  } else if (layer instanceof Polyline) {
    const style = styleIcon as PaintLineString;
    iconComponent = <Icon sx={{
      ...iconStyle,
      color: style ? style.color : undefined,
    }}>polyline</Icon>;
  } else if (layer instanceof Marker) {
    const htmlElement = (layer.getIcon() as DivIcon).options.html;

    const style = {
      ...iconStyle,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: "clip",
    };

    if (typeof htmlElement === 'string') {
      iconComponent = <Box sx={style}>
        <HTMLtoJSX element={htmlElement} />
      </Box>;
    } else if (htmlElement instanceof HTMLElement) {
      iconComponent = <Box sx={style}>
        <HTMLtoJSX element={htmlElement.innerHTML} />
      </Box>;
    } else {
      iconComponent = <PinDropOutlined sx={iconStyle} />;
    }
  } else if (layer instanceof Point) {
    iconComponent = <MyLocationOutlined sx={iconStyle} />;
  } else if (layer instanceof CircleMarker) {
    iconComponent = <CircleOutlined sx={iconStyle} />;
  } else if (layer instanceof TileLayer) {
    iconComponent = <WallpaperOutlined sx={iconStyle} />;
  } else if (layer instanceof GridLayer) {
    iconComponent = <Icon sx={iconStyle}>stacks</Icon>;
  } else if (layer instanceof LayerGroup) {
    const icons = new Set<string>();
    const types = new Set<string>();
    const colors = new Set<string>();
    layer.eachLayer((layer) => {
      if (layer instanceof Marker || layer instanceof CircleMarker || layer instanceof Point) {
        if ("svgItem" in layer && layer.svgItem) icons.add(layer.svgItem as string);
        types.add("point");
      } else if (layer instanceof Polygon || layer instanceof PolyMancha || layer instanceof PolyZas || layer instanceof PolyZss) {
        types.add("polygon");
      } else if (layer instanceof Polyline) {
        types.add("polyline");
      }

      if ("color" in layer.options) colors.add((layer.options.color as string) ?? "black");
    });

    if (icons.size === 1) {
      const icon = icons.values().next().value;
      iconComponent = <img
        width={(iconStyle.width as number) - 3}
        height={(iconStyle.height as number) - 3}
        src={icon}
        alt={icon}
        style={{
          borderRadius: "50%",
          backgroundColor: "white",
          border: "0.063rem solid black",
        }
        }
      />;
    } else if (icons.size > 1) { // TODO: Implementar quando tiver mais de um icone
      const icon = icons.values().next().value;
      iconComponent = <img
        width={(iconStyle.width as number) - 3}
        height={(iconStyle.height as number) - 3}
        src={icon}
        alt={icon}
        style={{
          borderRadius: "50%",
          backgroundColor: "white",
          border: "0.063rem solid black",
        }
        }
      />;

    } else {

      let color = undefined;
      let gradient = undefined;
      if (colors.size == 1) {
        color = colors.values().next().value;
      } else if (colors.size > 1) {
        const colorArray = Array.from(colors);
        const gradientColors = colorArray.join(', ');
        gradient = `linear-gradient(to right, ${gradientColors})`;
      }

      const style = {
        ...iconStyle,
        backgroundColor: color,
        background: gradient,
      };

      if (types.size > 1) iconComponent = <Icon sx={style}>stacks</Icon>;
      if (types.has("point")) iconComponent = <Icon sx={style}>circle</Icon>;
      if (types.has("polygon")) iconComponent = <RectangleOutlined sx={style} />;
      if (types.has("polyline")) iconComponent = <PolylineOutlined sx={style} />;

      iconComponent = <ErrorOutlineOutlined sx={style} />;
    }
  } else {
    iconComponent = <ErrorOutlineOutlined sx={iconStyle} />;
  }

  return <div onClick={setCheck}>{iconComponent}</div>;
};

const getCheck = (checked: boolean | undefined, motherShowing: boolean, setCheck: () => void) => {
  iconStyle.opacity = motherShowing ? 1 : 0.5;
  return <>
    {checked === true && <CheckBoxOutlined onClick={setCheck} sx={{ ...iconStyle, cursor: "pointer" }} />}
    {checked === false && <CheckBoxOutlineBlank onClick={setCheck} sx={{ ...iconStyle, cursor: "pointer" }} />}
    {checked === undefined && <IndeterminateCheckBoxOutlined onClick={setCheck} sx={{ ...iconStyle, cursor: "pointer" }} />}
  </>;
};

interface PropHTML {
  element: string;
}
export const HTMLtoJSX: React.FC<PropHTML> = ({ element }) => {
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nativeElement = document.createElement("div");
    nativeElement.innerHTML = element;

    if (divRef.current) {
      divRef.current.appendChild(nativeElement);
    }
  }, []);

  return <div ref={divRef}></div>;
};